import type { Container } from 'pixi.js';
import type { LayerStackAsset } from '../../layerstack/LayerStackLoader';
import { LayerStackSprite, PULSE_FPS } from '../../layerstack/LayerStackSprite';

/**
 * Impact sprites played once. `LayerStackSprite.advance` loops forever, so a
 * one-shot is a pulse cycle plus a stopwatch: the sprite is parked as soon as
 * one full cycle of playback time has elapsed.
 */
const PULSE_FRAME_MS = 1000 / PULSE_FPS;
/** Playback rate, not an art change: the authored 8fps cycle is shown at 2x. */
export const SPARK_TIME_SCALE = 2;
const POOL_MAX_PER_SLUG = 8;
/** Used when a rung ships an empty master cycle: still show the frame briefly. */
const FALLBACK_FRAME_COUNT = 4;

interface LiveShot {
  readonly sprite: LayerStackSprite;
  readonly durationMs: number;
  elapsedMs: number;
}

class SlugPool {
  readonly idle: LayerStackSprite[] = [];
  readonly live: LiveShot[] = [];
  readonly fixedSprites: LayerStackSprite[] = [];
  readonly fixedDurationMs = new Float32Array(POOL_MAX_PER_SLUG);
  readonly fixedElapsedMs = new Float32Array(POOL_MAX_PER_SLUG);
  readonly fixedLive = new Uint8Array(POOL_MAX_PER_SLUG);
  fixedCursor = POOL_MAX_PER_SLUG - 1;

  constructor(readonly asset: LayerStackAsset) {}
}

export class OneShotFx {
  private readonly pools = new Map<string, SlugPool>();
  /** Registration happens only during init; this fixed order avoids a Map iterator every mobile frame. */
  private readonly poolList: SlugPool[] = [];
  private fixedLiveCount = 0;

  constructor(private readonly layer: Container, private readonly prewarm = false) {}

  register(slug: string, asset: LayerStackAsset): void {
    if (this.pools.has(slug)) return;
    const pool = new SlugPool(asset);
    this.pools.set(slug, pool);
    this.poolList.push(pool);
    if (!this.prewarm) return;
    for (let i = 0; i < POOL_MAX_PER_SLUG; i += 1) {
      const sprite = new LayerStackSprite(asset);
      sprite.visible = false;
      this.layer.addChild(sprite);
      pool.fixedSprites.push(sprite);
    }
  }

  get liveCount(): number {
    if (this.prewarm) return this.fixedLiveCount;
    let total = 0;
    for (const pool of this.pools.values()) total += pool.live.length;
    return total;
  }

  /** Positions the sprite so its centre lands on (centreX, centreY), whole pixels. */
  spawn(slug: string, centreX: number, centreY: number): void {
    const pool = this.pools.get(slug);
    if (!pool) return;
    if (this.prewarm) {
      this.spawnFixed(pool, centreX, centreY);
      return;
    }
    const sprite = this.take(pool);
    sprite.visible = true;
    // setPulsing(true) early-returns when already playing; the off/on pair is
    // what rewinds the cycle to frame 0 for a reused sprite.
    sprite.setPulsing(false);
    sprite.setPulsing(true);
    sprite.setPixelPosition(centreX - sprite.targetWidth / 2, centreY - sprite.targetHeight / 2);
    const frameCount = sprite.pulse?.length ?? FALLBACK_FRAME_COUNT;
    pool.live.push({ sprite, durationMs: (frameCount * PULSE_FRAME_MS) / SPARK_TIME_SCALE, elapsedMs: 0 });
  }

  update(deltaMs: number): void {
    if (this.prewarm) {
      for (let poolIndex = 0; poolIndex < this.poolList.length; poolIndex += 1) {
        const pool = this.poolList[poolIndex]!;
        for (let slot = 0; slot < POOL_MAX_PER_SLUG; slot += 1) {
          if (pool.fixedLive[slot] === 0) continue;
          const sprite = pool.fixedSprites[slot]!;
          sprite.advance(deltaMs * SPARK_TIME_SCALE);
          const elapsed = pool.fixedElapsedMs[slot]! + deltaMs;
          pool.fixedElapsedMs[slot] = elapsed;
          if (elapsed < pool.fixedDurationMs[slot]!) continue;
          sprite.visible = false;
          pool.fixedLive[slot] = 0;
          this.fixedLiveCount -= 1;
        }
      }
      return;
    }
    for (const pool of this.pools.values()) {
      for (let i = pool.live.length - 1; i >= 0; i -= 1) {
        const shot = pool.live[i]!;
        shot.sprite.advance(deltaMs * SPARK_TIME_SCALE);
        shot.elapsedMs += deltaMs;
        if (shot.elapsedMs < shot.durationMs) continue;
        shot.sprite.visible = false;
        pool.live.splice(i, 1);
        pool.idle.push(shot.sprite);
      }
    }
  }

  reset(): void {
    if (this.prewarm) {
      for (let poolIndex = 0; poolIndex < this.poolList.length; poolIndex += 1) {
        const pool = this.poolList[poolIndex]!;
        pool.fixedLive.fill(0);
        pool.fixedCursor = POOL_MAX_PER_SLUG - 1;
        for (let slot = 0; slot < pool.fixedSprites.length; slot += 1) {
          pool.fixedSprites[slot]!.visible = false;
        }
      }
      this.fixedLiveCount = 0;
      return;
    }
    for (const pool of this.pools.values()) {
      for (const shot of pool.live) {
        shot.sprite.visible = false;
        pool.idle.push(shot.sprite);
      }
      pool.live.length = 0;
    }
  }

  /** Over budget the oldest live shot is recycled — pool-managed, never unbounded. */
  private take(pool: SlugPool): LayerStackSprite {
    const parked = pool.idle.pop();
    if (parked) return parked;
    if (pool.live.length >= POOL_MAX_PER_SLUG) {
      const oldest = pool.live.shift();
      if (oldest) return oldest.sprite;
    }
    const sprite = new LayerStackSprite(pool.asset);
    this.layer.addChild(sprite);
    return sprite;
  }

  /**
   * Mobile registration prebuilds every slot. Runtime uses a descending ring
   * (the same initial sprite order as the old idle.pop path) and typed state,
   * so a spawn performs no object/array allocation and a full pool still
   * replaces its oldest visual.
   */
  private spawnFixed(pool: SlugPool, centreX: number, centreY: number): void {
    const slot = pool.fixedCursor;
    pool.fixedCursor = slot === 0 ? POOL_MAX_PER_SLUG - 1 : slot - 1;
    const sprite = pool.fixedSprites[slot]!;
    if (pool.fixedLive[slot] === 0) this.fixedLiveCount += 1;
    pool.fixedLive[slot] = 1;
    sprite.visible = true;
    sprite.setPulsing(false);
    sprite.setPulsing(true);
    sprite.setPixelPosition(centreX - sprite.targetWidth / 2, centreY - sprite.targetHeight / 2);
    const frameCount = sprite.pulse?.length ?? FALLBACK_FRAME_COUNT;
    pool.fixedDurationMs[slot] = (frameCount * PULSE_FRAME_MS) / SPARK_TIME_SCALE;
    pool.fixedElapsedMs[slot] = 0;
  }
}
